import { NFL_TEAMS_TOTAL } from '@shared/constants';
import { ITeamDetails, TeamID } from '@shared/types';
import { getDivisionTeams } from 'app/slices/LeagueSlice';
import { breakUID } from 'helpers/IdHelpers';
import { sortDivisionalTeamsByPct } from 'helpers/SortHelpers';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ITeamStandings, NFLDivision, RootState } from 'types';
import DivisionTeam from '../team/DivisionTeam';
import styles from './Division.module.scss';

type DivisionProps = {
  className?: string;
  title: string;
  divisionId: NFLDivision;
  weeklyTeamStandings: ITeamStandings[];
  highlightTeamID?: TeamID;
  openFrom?: 'TOP' | 'LEFT';
};

export type DivisionalTeamData = {
  team: ITeamDetails;
  teamWeeklyResult: ITeamStandings;
};

const Division: React.FC<DivisionProps> = ({
  className = '',
  title = 'AFC/NFC Div',
  divisionId = NFLDivision.AFCEast,
  weeklyTeamStandings = [],
  highlightTeamID = '0',
  openFrom = 'TOP',
}) => {
  const baseCls = `${styles.DivisionContainer} ${openFrom} ${className}`;

  const divisionTeams = useSelector((state: RootState) => getDivisionTeams(state, divisionId));

  const [divTeamData, setDivTeamData] = useState<DivisionalTeamData[]>([]);

  useEffect(() => {
    if (divisionTeams.length !== 4 || weeklyTeamStandings.length !== NFL_TEAMS_TOTAL) return;

    const tempData: DivisionalTeamData[] = [];

    divisionTeams.forEach((team: ITeamDetails) => {
      tempData.push({
        team: team,
        teamWeeklyResult: weeklyTeamStandings.findLast(
          (teamStanding) => breakUID(teamStanding.uID).teamID === team.teamID
        ) as ITeamStandings,
      });
    });

    tempData.sort(sortDivisionalTeamsByPct);

    setDivTeamData(tempData);
  }, [divisionTeams, weeklyTeamStandings]);

  const renderDivisionLegend = () => (
    <div className={styles.DivisionLegend}>
      <span className={styles.DivisionTitle}>{title}</span>
      <span className={styles.DivisionLegendStats}>
        <span>W</span>
        <span>L</span>
        <span>T</span>
        <span>PCT</span>
        <span>PF</span>
        <span>PA</span>
        <span>HOME</span>
        <span>AWAY</span>
        <span>IN-DIV</span>
        <span>STRK</span>
      </span>
    </div>
  );

  const renderTeams = (teamsData: DivisionalTeamData[]) => {
    if (!teamsData) return <></>;

    return (
      <div className={styles.DivisionTeams}>
        {teamsData.map((divTeam) => (
          <DivisionTeam
            key={divTeam.team.teamID}
            team={divTeam.team}
            highlighted={divTeam.team.teamID === highlightTeamID}
            teamWeeklyResult={divTeam.teamWeeklyResult}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={baseCls}>
      {renderDivisionLegend()}
      {renderTeams(divTeamData)}
    </div>
  );
};

export default Division;
